/**
 * AutosaveStatusBadge
 * Compact status indicator for the Director Notes autosave state.
 *
 * States:
 *   idle    — grey dot, "Autosave on"
 *   dirty   — amber pulsing dot, "Unsaved…"
 *   saving  — spinning cloud icon, "Saving…"
 *   saved   — green checkcheck, "Saved HH:MM AM/PM"
 *   error   — red wifi-off icon, "Save failed"
 */

import { CloudUpload, CheckCheck, WifiOff, Dot } from "lucide-react";
import type { AutosaveStatus } from "@/hooks/useAutosave";

interface AutosaveStatusBadgeProps {
  status: AutosaveStatus;
  lastSavedAt: string | null;
}

function formatSavedTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function AutosaveStatusBadge({ status, lastSavedAt }: AutosaveStatusBadgeProps) {
  if (status === "idle") {
    return (
      <span className="flex items-center gap-1 text-[10px] text-zinc-600 select-none">
        <Dot className="w-3 h-3 text-zinc-600" />
        Autosave on
      </span>
    );
  }

  if (status === "dirty") {
    return (
      <span className="flex items-center gap-1 text-[10px] text-amber-500/70 select-none">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500/70 animate-pulse inline-block" />
        Unsaved…
      </span>
    );
  }

  if (status === "saving") {
    return (
      <span className="flex items-center gap-1 text-[10px] text-amber-400 select-none">
        <CloudUpload className="w-3 h-3 animate-pulse" />
        Saving…
      </span>
    );
  }

  if (status === "saved") {
    return (
      <span className="flex items-center gap-1 text-[10px] text-emerald-500 select-none">
        <CheckCheck className="w-3 h-3" />
        Saved {lastSavedAt ? formatSavedTime(lastSavedAt) : ""}
      </span>
    );
  }

  // error
  return (
    <span className="flex items-center gap-1 text-[10px] text-red-400 select-none" title="Autosave failed — check network">
      <WifiOff className="w-3 h-3" />
      Save failed
    </span>
  );
}
